"use client";

/**
 * MarpImageStylePicker — compact 4-option dropdown for selecting the
 * illustration style used when the user opts into AI-generated images on
 * the Marp deck.
 *
 * Mirrors `MarpTemplatePicker`'s visual language (Radix `DropdownMenu`,
 * cream surface, hairline border, ink ramp + orange accent on focus) so
 * the tab strip reads as a single cohesive control row. The picker is
 * mounted by `<MarpDeckEditor>` immediately AFTER the AI-images toggle
 * and is hidden whenever the toggle is OFF — there is no useful style to
 * pick when image generation is disabled.
 *
 * Options map 1:1 to the BE `illustration_style` literal (BE-Resolver
 * agent, Wave 6):
 *   - `editorial`   default; flat-iso brand-coherent
 *   - `playful`     hand-drawn, doodle
 *   - `photoreal`   editorial photography
 *   - `abstract`    geometric composition
 *
 * Wave 6 — May 26, 2026.
 */

import { Camera, ChevronDown, Layers, Shapes, Smile } from "lucide-react";
import type * as React from "react";

import { cn } from "../lib/cn";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "../primitives/dropdown-menu";

/** Public union: the four supported illustration styles. */
export type MarpIllustrationStyle =
  | "editorial"
  | "playful"
  | "photoreal"
  | "abstract";

export interface MarpImageStylePickerProps {
  readonly value: MarpIllustrationStyle;
  readonly onChange: (next: MarpIllustrationStyle) => void;
  /** Greyed out + non-interactive (e.g. compile in flight). */
  readonly disabled?: boolean;
  readonly className?: string;
}

interface StyleOption {
  readonly value: MarpIllustrationStyle;
  readonly label: string;
  readonly description: string;
  readonly icon: React.ComponentType<{
    readonly className?: string;
    readonly strokeWidth?: number;
    readonly "aria-hidden"?: boolean;
  }>;
}

/**
 * Single source of truth for the four options. Kept module-local so
 * a misuse of the `value` union (typo) trips the TS check via the
 * `MarpIllustrationStyle` literal rather than a string-only fallback.
 */
const STYLE_OPTIONS: ReadonlyArray<StyleOption> = [
  {
    value: "editorial",
    label: "Editoriale",
    description: "Flat isometric, coerente con il brand",
    icon: Layers,
  },
  {
    value: "playful",
    label: "Playful",
    description: "Disegno a mano, tono giocoso",
    icon: Smile,
  },
  {
    value: "photoreal",
    label: "Fotorealistico",
    description: "Fotografia editoriale, luce naturale",
    icon: Camera,
  },
  {
    value: "abstract",
    label: "Astratto",
    description: "Composizione geometrica minimal",
    icon: Shapes,
  },
];

function findOption(value: MarpIllustrationStyle): StyleOption {
  // The union prevents bad values at compile-time. At runtime we still
  // fall back to the default option rather than throw — a defensive
  // belt-and-suspenders that mirrors `MarpTemplatePicker.deriveLabel`.
  return STYLE_OPTIONS.find((o) => o.value === value) ?? STYLE_OPTIONS[0]!;
}

export const MarpImageStylePicker: React.FC<MarpImageStylePickerProps> = ({
  value,
  onChange,
  disabled = false,
  className,
}) => {
  const selected = findOption(value);
  const SelectedIcon = selected.icon;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        type="button"
        disabled={disabled}
        aria-label="Stile illustrazioni AI"
        className={cn(
          // Mirrors MarpTemplatePicker trigger: h-8/md:h-7, hairline,
          // cream-aware hover, orange focus ring.
          "inline-flex h-8 max-w-[180px] items-center gap-1.5 truncate rounded-md border border-hairline bg-white/85 px-2 font-medium text-[11px] text-ink-2 transition-colors md:h-7",
          "hover:bg-white hover:text-ink-1",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45",
          "disabled:cursor-not-allowed disabled:opacity-50",
          "data-[state=open]:border-ando-orange/40 data-[state=open]:bg-white data-[state=open]:text-ink-1",
          className,
        )}
      >
        <SelectedIcon
          className="h-3 w-3 shrink-0 text-ando-orange"
          aria-hidden
          strokeWidth={2}
        />
        <span className="truncate" title={selected.label}>
          {selected.label}
        </span>
        <ChevronDown
          className="h-3 w-3 shrink-0 text-ink-3"
          aria-hidden
          strokeWidth={2}
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={6}
        className="min-w-[220px] max-w-[280px] bg-cream"
      >
        <DropdownMenuLabel>Stile illustrazioni</DropdownMenuLabel>
        {STYLE_OPTIONS.map((option) => (
          <StyleItem
            key={option.value}
            option={option}
            selected={option.value === value}
            onSelect={() => onChange(option.value)}
          />
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
MarpImageStylePicker.displayName = "MarpImageStylePicker";

interface StyleItemProps {
  readonly option: StyleOption;
  readonly selected: boolean;
  readonly onSelect: () => void;
}

const StyleItem: React.FC<StyleItemProps> = ({
  option,
  selected,
  onSelect,
}) => {
  const Icon = option.icon;
  return (
    <DropdownMenuItem
      onSelect={() => onSelect()}
      className={cn(
        "flex items-start gap-2 py-1.5",
        selected && "font-semibold text-ando-orange",
      )}
    >
      <Icon
        className={cn(
          "mt-0.5 h-3.5 w-3.5 shrink-0",
          selected ? "text-ando-orange" : "text-ink-3",
        )}
        aria-hidden
        strokeWidth={2}
      />
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="truncate font-medium text-[12px]">{option.label}</span>
        <p className="line-clamp-2 text-[10.5px] text-ink-3">
          {option.description}
        </p>
      </div>
    </DropdownMenuItem>
  );
};
StyleItem.displayName = "MarpImageStylePicker.Item";
